import { useState, useCallback, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '';

function api(path, token, options = {}) {
  return fetch(`${API_URL}${path}`, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    ...options,
  }).then((r) => r.json());
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
}

export default function ContentManagement({ token }) {
  const [subTab, setSubTab] = useState('faq');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null = list, 'new' = new form, item object = edit form
  const [saving, setSaving] = useState(false);

  // Form fields
  const [formTitle, setFormTitle] = useState('');
  const [formBody, setFormBody] = useState('');
  const [formSortOrder, setFormSortOrder] = useState(0);
  const [formIsPublished, setFormIsPublished] = useState(true);
  const [formCategory, setFormCategory] = useState('');

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = subTab === 'faq' ? '/admin/faq' : '/admin/announcements';
      const data = await api(endpoint, token);
      setItems(subTab === 'faq' ? (data.faq || []) : (data.announcements || []));
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [subTab, token]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const openNew = () => {
    setFormTitle('');
    setFormBody('');
    setFormSortOrder(items.length);
    setFormIsPublished(true);
    setFormCategory('');
    setEditing('new');
  };

  const openEdit = (item) => {
    setFormTitle(item.title);
    setFormBody(item.body);
    setFormSortOrder(item.sortOrder ?? 0);
    setFormIsPublished(item.isPublished ?? true);
    setFormCategory(item.category || '');
    setEditing(item);
  };

  const handleSave = async () => {
    if (!formTitle.trim() || formBody === undefined) {
      alert('제목과 본문을 입력하세요.');
      return;
    }
    setSaving(true);
    try {
      const isNew = editing === 'new';
      const contentId = isNew ? undefined : editing.contentId;
      const endpoint = subTab === 'faq'
        ? `/admin/faq/${contentId || ''}`
        : `/admin/announcements/${contentId || ''}`;

      const bodyData = {
        title: formTitle.trim(),
        body: formBody,
        sortOrder: formSortOrder,
        isPublished: formIsPublished,
      };
      if (subTab === 'announcements') {
        bodyData.category = formCategory;
      }

      const res = await api(endpoint, token, {
        method: 'PUT',
        body: JSON.stringify(bodyData),
      });

      if (res.error) {
        alert(`저장 실패: ${res.error}`);
      } else {
        setEditing(null);
        await fetchItems();
      }
    } catch (e) {
      alert('저장 중 오류가 발생했습니다.');
    }
    setSaving(false);
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`"${item.title}" 항목을 삭제하시겠습니까?`)) return;
    try {
      const endpoint = subTab === 'faq'
        ? `/admin/faq/${item.contentId}`
        : `/admin/announcements/${item.contentId}`;
      const res = await api(endpoint, token, { method: 'DELETE' });
      if (res.error) {
        alert(`삭제 실패: ${res.error}`);
      } else {
        await fetchItems();
      }
    } catch (e) {
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  // Edit/New form
  if (editing) {
    return (
      <div className="payments-page">
        <button className="back-btn" onClick={() => setEditing(null)}>
          &larr; 목록으로
        </button>

        <div className="detail-card">
          <h3>{editing === 'new' ? '새로 추가' : '수정'}</h3>
          <div className="content-form">
            <div className="form-group">
              <label>제목</label>
              <input
                type="text"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                placeholder="제목을 입력하세요"
              />
            </div>
            <div className="form-group">
              <label>본문</label>
              <textarea
                value={formBody}
                onChange={(e) => setFormBody(e.target.value)}
                placeholder="본문을 입력하세요"
                rows={8}
              />
            </div>
            <div className="form-row-inline">
              <div className="form-group">
                <label>순서</label>
                <input
                  type="number"
                  value={formSortOrder}
                  onChange={(e) => setFormSortOrder(parseInt(e.target.value) || 0)}
                  min="0"
                />
              </div>
              <div className="form-group">
                <label>공개여부</label>
                <select value={formIsPublished ? 'true' : 'false'} onChange={(e) => setFormIsPublished(e.target.value === 'true')}>
                  <option value="true">공개</option>
                  <option value="false">비공개</option>
                </select>
              </div>
              {subTab === 'announcements' && (
                <div className="form-group">
                  <label>카테고리</label>
                  <input
                    type="text"
                    value={formCategory}
                    onChange={(e) => setFormCategory(e.target.value)}
                    placeholder="카테고리"
                  />
                </div>
              )}
            </div>
            <button
              className="load-btn"
              onClick={handleSave}
              disabled={saving}
              style={{ width: '100%', marginTop: 16 }}
            >
              {saving ? '저장 중...' : '저장'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="payments-page">
      <div className="content-header">
        <div className="sub-tab-nav">
          <button
            className={`tab-btn ${subTab === 'faq' ? 'active' : ''}`}
            onClick={() => { setSubTab('faq'); }}
          >
            FAQ
          </button>
          <button
            className={`tab-btn ${subTab === 'announcements' ? 'active' : ''}`}
            onClick={() => { setSubTab('announcements'); }}
          >
            공지사항
          </button>
        </div>
        <button className="load-btn" onClick={openNew}>
          + 새로 추가
        </button>
      </div>

      {loading && <p style={{ padding: 24 }}>로딩 중...</p>}

      {!loading && items.length > 0 && (
        <div className="payment-table-wrap">
          <table className="payment-table">
            <thead>
              <tr>
                <th style={{ width: 60 }}>순서</th>
                <th>제목</th>
                {subTab === 'announcements' && <th style={{ width: 100 }}>카테고리</th>}
                <th style={{ width: 80 }}>공개</th>
                <th style={{ width: 110 }}>수정일</th>
                <th style={{ width: 120 }}>관리</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.contentId}>
                  <td>{item.sortOrder ?? 0}</td>
                  <td>{item.title}</td>
                  {subTab === 'announcements' && <td>{item.category || '-'}</td>}
                  <td>
                    <span className={`badge ${item.isPublished ? 'badge-completed' : 'badge-refunded'}`}>
                      {item.isPublished ? '공개' : '비공개'}
                    </span>
                  </td>
                  <td>{formatDate(item.updatedAt)}</td>
                  <td>
                    <div className="action-btns">
                      <button className="action-edit-btn" onClick={() => openEdit(item)}>수정</button>
                      <button className="action-delete-btn" onClick={() => handleDelete(item)}>삭제</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="empty-state">
          <p>{subTab === 'faq' ? 'FAQ가' : '공지사항이'} 없습니다. 새로 추가해보세요.</p>
        </div>
      )}
    </div>
  );
}
